import React, { useState, useRef } from 'react';
import { Upload, Button, Input, message } from 'antd';
import { UploadOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import axios from 'axios';
import './ImageUploader.css';

const ImageUploader = ({ 
  value, 
  onChange, 
  label = "Imagen",
  placeholder = "https://ejemplo.com/imagen.jpg",
  uploadEndpoint = "https://api.mrcirp.com/public/upload-imagen-campana",
  showPreview = true,
  maxSizeMB = 5,
  recommendedSize = "1920px x 1080px",
  autoUpload = false // Si es false, solo muestra vista previa sin subir al servidor
}) => {
  const [uploading, setUploading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null); // URL local para vista previa antes de subir
  const [selectedFileName, setSelectedFileName] = useState(''); // Nombre del archivo seleccionado
  const fileInputRef = useRef(null);

  // Validar tamaño del archivo
  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Solo se permiten archivos de imagen!');
      return false;
    }
    const isLtMaxSize = file.size / 1024 / 1024 < maxSizeMB;
    if (!isLtMaxSize) {
      message.error(`La imagen debe ser menor a ${maxSizeMB}MB!`);
      return false;
    }
    return true;
  };

  // Crear vista previa local antes de subir
  const createLocalPreview = (file) => {
    setSelectedFileName(file.name); // Guardar el nombre del archivo
    const reader = new FileReader();
    reader.onloadend = () => {
      setLocalPreviewUrl(reader.result);
      // También actualizar el valor inmediatamente para que se vea en la vista previa del banner
      onChange(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Manejar selección de archivo (solo vista previa local, sin subir al servidor)
  const handleFileSelect = (file) => {
    if (!beforeUpload(file)) {
      return;
    }

    // Solo crear vista previa local - NO subir al servidor
    createLocalPreview(file);
  };

  // Manejar subida de archivo al servidor (solo si autoUpload es true)
  const handleUpload = async (file) => {
    if (!beforeUpload(file)) {
      return;
    }

    // Si autoUpload es false, solo mostrar vista previa local
    if (!autoUpload) {
      handleFileSelect(file);
      return;
    }

    // Crear vista previa local INMEDIATAMENTE (sin esperar al servidor)
    createLocalPreview(file);

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'campaign');

    try {
      const response = await axios.post(uploadEndpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('Respuesta del servidor:', response.data);
      
      let imageUrl = null;
      if (response.data && response.data.url) {
        imageUrl = response.data.url;
      } else if (response.data && response.data.imagen) {
        imageUrl = response.data.imagen;
      } else if (response.data && typeof response.data === 'string') {
        imageUrl = response.data;
      }
      
      if (imageUrl) {
        console.log('URL de imagen recibida:', imageUrl);
        // Verificar que la URL sea válida antes de actualizar
        if (imageUrl && imageUrl.trim() !== '') {
          // Actualizar con la URL del servidor cuando esté lista
          onChange(imageUrl);
          message.success('Imagen subida exitosamente!');
        } else {
          throw new Error('URL de imagen inválida recibida del servidor');
        }
      } else {
        console.error('No se encontró URL en la respuesta:', response.data);
        throw new Error('No se recibió la URL de la imagen');
      }
    } catch (error) {
      console.error('Error al subir imagen:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error desconocido';
      message.error(`Error al subir la imagen: ${errorMessage}. Puedes pegar una URL manual.`);
      // Si falla la subida, mantener la vista previa local para que el usuario pueda seguir trabajando
    } finally {
      setUploading(false);
    }
  };

  // Manejar cambio de URL manual
  const handleUrlChange = (e) => {
    onChange(e.target.value);
  };

  // Mostrar vista previa
  const handlePreview = () => {
    if (value) {
      setPreviewImage(value);
      setPreviewVisible(true);
    }
  };

  // Eliminar imagen
  const handleRemove = () => {
    onChange('');
    setLocalPreviewUrl(null); // Limpiar vista previa local también
    setSelectedFileName(''); // Limpiar nombre del archivo
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="image-uploader-container">
      <label className="image-uploader-label">
        <strong>{label}</strong>
        {recommendedSize && (
          <span className="recommended-size"> (Recomendado: {recommendedSize})</span>
        )}
      </label>
      
      <div className="image-uploader-actions">
        {/* Botón para subir archivo */}
        <Upload
          beforeUpload={(file) => {
            handleUpload(file);
            return false; // Prevenir subida automática
          }}
          showUploadList={false}
          accept="image/*"
        >
          <Button 
            icon={<UploadOutlined />} 
            loading={uploading}
            disabled={uploading}
          >
            {uploading ? 'Subiendo...' : autoUpload ? 'Subir Imagen' : 'Seleccionar Imagen'}
          </Button>
        </Upload>

        {/* Botón para ver vista previa */}
        {value && showPreview && (
          <Button 
            icon={<EyeOutlined />} 
            onClick={handlePreview}
            style={{ marginLeft: '8px' }}
          >
            Ver
          </Button>
        )}

        {/* Botón para eliminar */}
        {value && (
          <Button 
            icon={<DeleteOutlined />} 
            danger
            onClick={handleRemove}
            style={{ marginLeft: '8px' }}
          >
            Eliminar
          </Button>
        )}
      </div>

      {/* Input para URL manual */}
      <Input
        value={value || ''}
        onChange={handleUrlChange}
        placeholder={placeholder}
        style={{ marginTop: '8px' }}
        allowClear
      />

      {/* Vista previa pequeña de la imagen - prioriza localPreviewUrl si existe, sino usa value */}
      {(localPreviewUrl || value) && showPreview && (
        <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img 
            src={localPreviewUrl || value} 
            alt="Preview" 
            style={{
              width: '60px',
              height: '60px',
              objectFit: 'cover',
              borderRadius: '4px',
              border: '1px solid #d9d9d9'
            }}
            onError={(e) => {
              // Si falla la imagen, intentar con la otra URL
              if (localPreviewUrl && e.target.src !== localPreviewUrl) {
                e.target.src = localPreviewUrl;
              } else if (value && e.target.src !== value) {
                e.target.src = value;
              } else {
                e.target.style.display = 'none';
              }
            }}
          />
          {selectedFileName && (
            <span style={{ 
              fontSize: '12px', 
              color: '#666',
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {selectedFileName}
            </span>
          )}
        </div>
      )}

      {/* Modal de vista previa (simple) */}
      {previewVisible && (
        <div 
          className="image-preview-modal"
          onClick={() => setPreviewVisible(false)}
        >
          <div className="image-preview-modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={previewImage} alt="Preview" />
            <Button 
              onClick={() => setPreviewVisible(false)}
              style={{ marginTop: '10px' }}
            >
              Cerrar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;

